import {
  type Environment,
  type RecordSourceSelectorProxy,
} from "relay-runtime";

import type { Record } from "relay-runtime/store/RelayStoreTypes";

// Type for accessing Relay's internal _records property
export interface RelaySourceWithRecords {
  _records: Map<string, Record>;
}

/**
 * Finds all connection IDs in a Relay store for a given connection key and parent record
 * @param relayEnvironment - The Relay environment containing the store
 * @param connectionKey - The connection key to search for (e.g., "GroupsTable_roleAssignments")
 * @param parentId - The parent record ID to search within (e.g., "client:root")
 * @returns Array of connection record IDs that match the connection key
 */
export function findAllConnectionIds(
  relayEnvironment: Environment,
  connectionKey: string,
  parentId: string,
) {
  const ids: string[] = [];
  // Cast to access the undocumented _records property
  const value = (
    relayEnvironment.getStore().getSource() as unknown as RelaySourceWithRecords
  )._records.get(parentId);

  if (value !== undefined) {
    Object.entries(value).forEach(function (param) {
      if (param[0].startsWith("__" + connectionKey + "_connection")) {
        // Type guard to ensure param[1] has the expected structure
        const connectionValue = param[1] as Record;
        if (
          typeof connectionValue === "object" &&
          connectionValue &&
          "__ref" in connectionValue
        ) {
          const ref = connectionValue.__ref;
          if (typeof ref === "string") {
            ids.push(ref);
          }
        }
        return;
      }
    });
  }
  return ids;
}

/**
 * Finds all connection IDs and invalidates them in the Relay store
 * @param relayEnvironment - The Relay environment
 * @param connectionKey - The connection key to search for
 * @param parentId - The parent record ID to search within
 * @param store - The Relay store from the updater function
 */
export function invalidateConnections(
  relayEnvironment: Environment,
  connectionKey: string,
  parentId: string,
  store: RecordSourceSelectorProxy,
) {
  const ids = findAllConnectionIds(relayEnvironment, connectionKey, parentId);

  ids.forEach((id) => {
    const record = store.get(id);
    if (record) {
      record.invalidateRecord();
    }
  });
}

/**
 * Invalidates every cached instance of a plain (non-`@connection`) list field,
 * across all the argument sets it has been fetched with.
 *
 * `invalidateConnections` only reaches connections Relay labelled with
 * `@connection`. A field paged by explicit cursors instead — a users field
 * taking filterElement and first — is stored as one record per argument set
 * hanging off its parent, with no connection key to look them up by, so match
 * on the field name instead. Queries reading any of them are stale from here
 * on and refetch the next time they mount.
 *
 * @param relayEnvironment - The Relay environment containing the store
 * @param fieldName - Unaliased field name to match, e.g. "users"
 * @param parentId - Record holding the field (e.g. "client:root" for a root field)
 * @param store - The Relay store from the updater function
 * @param argsInclude - Restricts the match to argument sets whose serialised
 *   form contains this string, so a change that only affects some of a field's
 *   pages leaves the rest alone. Arguments are serialised as JSON, so pass a
 *   JSON-encoded value (`JSON.stringify(name)`) to match a filter value whole
 *   rather than as a prefix of a longer one.
 */
export function invalidateFieldRecords(
  relayEnvironment: Environment,
  fieldName: string,
  parentId: string,
  store: RecordSourceSelectorProxy,
  argsInclude?: string,
) {
  // Cast to access the undocumented _records property, as findAllConnectionIds
  // does — Relay exposes no public way to enumerate a record's fields.
  const parent = (
    relayEnvironment.getStore().getSource() as unknown as RelaySourceWithRecords
  )._records.get(parentId);

  if (parent === undefined) {
    return;
  }

  Object.entries(parent).forEach(([key, value]) => {
    // Storage keys are the field name, optionally followed by its serialised
    // arguments: "users" or "users(filterElement:{...},first:5)".
    if (key !== fieldName && !key.startsWith(`${fieldName}(`)) {
      return;
    }
    if (argsInclude !== undefined && !key.includes(argsInclude)) {
      return;
    }
    if (typeof value === "object" && value && "__ref" in value) {
      const ref = (value as Record).__ref;
      if (typeof ref === "string") {
        store.get(ref)?.invalidateRecord();
      }
    }
  });
}
